import { Button, Card, Form, Select, Skeleton, Switch } from "antd";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import Chart from "react-google-charts";
import { useQuery } from "react-query";
import { columnChartConfigurationOptions } from "../../config";
import { SettingContext } from "../../routes";
import HTTP from "../../services/httpService";
import { downloadCSV, formatDate, hotelTypeOptions } from "../../services/util";
import { DashboardGrids } from "../Home/Home";

const { Option } = Select;

const xAxis = [
    { key: "monthly", title: "/Month" },
    { key: "yearly", title: "/Year" },
    { key: "days", title: "/Day" },
];

const allGrids = [...DashboardGrids];
const yAxis = allGrids.filter(
    (e) => e.key !== "hotel_total" && e.key !== "upcoming_total",
);

function HotelAnalytics() {
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const [form] = Form.useForm();
    // selection settings
    const { selectedDates } = useContext(SettingContext);
    const [start_date, end_date] = selectedDates;

    // chart settings
    const [chartData, setChartData] = useState([]);
    const [chartType, setChartType] = useState("LineChart");
    const [yAxisOptions, setYAxisOptions] = useState(
        yAxis.reduce((prev, cur) => {
            if (
                cur.key !== "android_user_count" &&
                cur.key !== "guest_user_count" &&
                cur.key !== "ios_user_count" &&
                cur.key !== "staff_user_count"
            ) {
                prev.push(cur);
            }
            return prev;
        }, []),
    );
    const [xAxisOptions, setXAxisOptions] = useState(xAxis);
    const [yAxisSelection, setYAxisSelection] = useState(yAxisOptions[0].key);
    const [xAxisSelection, setXAxisSelection] = useState(xAxisOptions[0].key);

    const [hotelType, setHotelType] = useState(hotelTypeOptions[0].key);

    const [formData, setFormData] = useState({
        staff_id,
        all_selected: false,
        start_date: formatDate(start_date),
        end_date: formatDate(end_date),
        y_param: yAxisSelection,
        x_param: xAxisSelection,
        hotel_type: "all",
    });

    const { status: hotelStatus, data: hotelData } = useQuery(
        "analytics-hotels",
        async () => {
            return await HTTP("/dashboard/getHotels", {
                staff_id,
                hotel_type: "all",
            });
        },
    );

    const { status: kioskHotelStatus, data: kioskHotelData } = useQuery(
        "kiosk-hotels",
        async () => {
            return await HTTP("/dashboard/getKioskHotels", {
                staff_id,
                limit: 1000,
                page: 0,
            });
        },
    );

    const { status: OperationalHotelStatus, data: OperationalHotelData } =
        useQuery("operational-hotels", async () => {
            return await HTTP("/dashboard/getOperationalHotels", {
                staff_id,
                limit: 1000,
                page: 0,
            });
        });

    const [hotelOptions, setHotelOptions] = useState([]);

    const {
        status: chartStatus,
        data: chartRes,
        refetch: getChartData,
    } = useQuery(
        "analyticsChart",
        async () => {
            return await HTTP("/analytics/getAnalyticsData", { ...formData });
        },
        { enabled: false },
    );

    useEffect(() => {
        if (hotelStatus === "success") {
            setHotelOptions(hotelData.data);
            const updatedFormData = {
                ...formData,
                hotel_ids: [hotelData.data[0].hotel_id],
            };
            setFormData(updatedFormData);
            form.setFieldsValue({ hotel_id: [hotelData.data[0].hotel_id] });
        }
    }, [hotelStatus, hotelData]);

    useEffect(() => {
        const [start_date, end_date] = selectedDates;
        const updatedFormData = { ...formData };
        updatedFormData.start_date = formatDate(start_date);
        updatedFormData.end_date = formatDate(end_date);
        if (updatedFormData.chart_for === "comparison") {
            const diff = moment(end_date).diff(moment(start_date), "days");
            const previous_start_date = moment(start_date).subtract(
                diff === 0 ? 1 : diff,
                "days",
            );
            const previous_end_date = moment(start_date).subtract(1, "days");
            updatedFormData.previous_start_date =
                formatDate(previous_start_date);
            updatedFormData.previous_end_date = formatDate(previous_end_date);
        }
        setFormData(updatedFormData);
    }, [selectedDates]);

    useEffect(() => {
        getChartData();
    }, [formData]);

    useEffect(() => {
        if (chartStatus === "success") {
            const { data: chartData, success } = chartRes;
            let data = [];
            if (success) {
                data = chartData.map((e, index) => {
                    if (index > 0) {
                        e[1] = parseFloat(e[1]);
                    }
                    return e;
                });
            }
            setChartData(data);
        }
    }, [chartStatus, chartRes]);

    function handleYAxisChange(event) {
        setYAxisSelection(event);
        setFormData({ ...formData, y_param: event });
        const tempOptions = [...xAxis];
        const disabledOptionsXAxis = tempOptions.reduce((prev, cur) => {
            prev.push(cur);
            if (
                event === "android_user_count" ||
                event === "guest_user_count" ||
                event === "ios_user_count" ||
                event === "staff_user_count"
            ) {
                if (cur.key === "hotel_name") {
                    prev.pop();
                }
            }
            return prev;
        }, []);
        setXAxisOptions(disabledOptionsXAxis);
    }

    function handleXAxisChange(event) {
        setXAxisSelection(event);
        setFormData({ ...formData, x_param: event });
        setYAxisOptions(updateYAxisOptions(event, formData.hotel_type));
    }

    function updateYAxisOptions(x_param, hotel_type) {
        let tempOptions = [...yAxis];
        tempOptions = tempOptions.reduce((prev, cur) => {
            if (
                cur.key === "android_user_count" ||
                cur.key === "guest_user_count" ||
                cur.key === "ios_user_count" ||
                cur.key === "staff_user_count"
            ) {
                if (x_param !== "hotel_name") {
                    prev.push(cur);
                }
            } else {
                prev.push(cur);
            }
            return prev;
        }, []);

        tempOptions = tempOptions.reduce((prev, cur) => {
            prev.push(cur);
            if (
                cur.key === "dinning_order_count" ||
                cur.key === "hk_order_count" ||
                cur.key === "hk_ticket_count" ||
                cur.key === "mnt_ticket_count"
            ) {
                if (hotel_type === "kiosk") {
                    prev.pop();
                }
            } else {
                if (hotel_type === "operation") {
                    prev.pop();
                }
            }
            return prev;
        }, []);
        return tempOptions;
    }

    function handleCompare(enabled) {
        if (enabled) {
            const { start_date, end_date } = formData;
            const diff = moment(end_date).diff(moment(start_date), "days");
            const previous_start_date = moment(start_date).subtract(
                diff === 0 ? 1 : diff,
                "days",
            );
            const previous_end_date = moment(start_date).subtract(1, "days");
            setFormData({
                ...formData,
                chart_for: "comparison",
                previous_start_date: formatDate(previous_start_date),
                previous_end_date: formatDate(previous_end_date),
            });
        } else {
            const updatedFormData = { ...formData };
            delete updatedFormData["chart_for"];
            delete updatedFormData["previous_start_date"];
            delete updatedFormData["previous_end_date"];
            setFormData(updatedFormData);
        }
    }

    function handleHotelCompare(enabled) {
        if (enabled) {
            setFormData({ ...formData, chart_for: "hotel_comparison" });
        } else {
            const updatedFormData = { ...formData };
            delete updatedFormData["chart_for"];
            delete updatedFormData["previous_start_date"];
            delete updatedFormData["previous_end_date"];
            setFormData(updatedFormData);
        }
    }

    function updateSelectedYAxis(newYAxisOptions) {
        const found = newYAxisOptions.find((e) => e.key === yAxisSelection);
        if (!found) {
            setYAxisSelection(newYAxisOptions[0].key);
            return newYAxisOptions[0].key;
        }
        return null;
    }

    function handleHotelTypeSelection(e) {
        setHotelType(e);
        let updatedFormData = {
            ...formData,
            hotel_type: e,
        };
        if (e === "all") {
            if (hotelStatus === "success") {
                setHotelOptions(hotelData.data);
                updatedFormData.hotel_ids = [hotelData.data[0].hotel_id];
                form.setFieldsValue({ hotel_id: [hotelData.data[0].hotel_id] });
            }
        } else if (e === "kiosk") {
            if (kioskHotelStatus === "success") {
                setHotelOptions(kioskHotelData.data);
                updatedFormData.hotel_ids = [kioskHotelData.data[0].id];
                form.setFieldsValue({
                    hotel_id: [kioskHotelData.data[0].id],
                });
            }
        } else {
            if (OperationalHotelStatus === "success") {
                setHotelOptions(OperationalHotelData.data);
                updatedFormData.hotel_ids = [OperationalHotelData.data[0].id];
                form.setFieldsValue({
                    hotel_id: [OperationalHotelData.data[0].id],
                });
            }
        }
        const updatedYAxisParam = updateYAxisOptions(formData.x_param, e);

        setYAxisOptions(updatedYAxisParam);

        const newSelection = updateSelectedYAxis(updatedYAxisParam);

        if (newSelection) {
            updatedFormData.y_param = newSelection;
        }
        setFormData(updatedFormData);
    }

    function handleHotelSelection(e) {
        setFormData({
            ...formData,
            hotel_ids: e,
            all_selected: e.length > 0 ? false : true,
        });
    }

    function handleChartSelection(enabled) {
        if (enabled) {
            setChartType("Bar");
        } else {
            setChartType("LineChart");
        }
    }

    async function Download() {
        const res = await HTTP("/analytics/getAnalyticsDataReport", {
            ...formData,
        });
        downloadCSV(res, "Analytics");
    }

    return (
        <Card className="chartCard chartWithDatePicker">
            <Button
                type="primary"
                onClick={Download}
                style={{ marginLeft: "auto" }}
            >
                Download
            </Button>
            <Form
                labelCol={{
                    span: 4,
                }}
                wrapperCol={{
                    span: 6,
                }}
                form={form}
            >
                <Form.Item label="Hotel Type">
                    <Select
                        placeholder="Hotel Type"
                        className="jira-dashboard-select"
                        onChange={handleHotelTypeSelection}
                        defaultValue={hotelType}
                    >
                        {hotelTypeOptions.map((e) => (
                            <Option key={e.key}>{e.title}</Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item label="Hotels" name="hotel_id">
                    <Select
                        id="hotel_id"
                        placeholder="Hotel Name"
                        className="jira-dashboard-select"
                        showSearch
                        mode="multiple"
                        onChange={handleHotelSelection}
                        filterOption={(input, option) =>
                            option.children
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }
                        loading={hotelOptions.length == 0}
                    >
                        {hotelOptions.map((h) => (
                            <Option
                                key={h.hotel_id || h.id}
                                value={h.hotel_id || h.id}
                            >
                                {`${h.hotel_id || h.id}-${
                                    h.hotel_name || h.name
                                } - ${h.city}`}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item label="X-Axis">
                    <Select
                        placeholder="x-axis"
                        className="jira-dashboard-select"
                        onChange={handleXAxisChange}
                        defaultValue={xAxisSelection}
                        value={xAxisSelection}
                    >
                        {xAxisOptions.map((e) => (
                            <Option key={e.key}>{e.title}</Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item label="Y-Axis">
                    <Select
                        showSearch
                        placeholder="y-axis"
                        className="jira-dashboard-select"
                        onChange={handleYAxisChange}
                        filterOption={(input, option) =>
                            option.children
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }
                        defaultValue={yAxisSelection}
                        value={yAxisSelection}
                    >
                        {yAxisOptions.map((e) => (
                            <Option key={e.key}>{e.title}</Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item label="Compare">
                    <Switch
                        disabled={formData?.chart_for === "hotel_comparison"}
                        className="jira-dashboard-select"
                        onChange={handleCompare}
                    />
                </Form.Item>
                <Form.Item label="Compare Hotels">
                    <Switch
                        disabled={
                            formData?.chart_for === "comparison" ||
                            formData?.hotel_ids?.length <= 1
                        }
                        className="jira-dashboard-select"
                        onChange={handleHotelCompare}
                    />
                </Form.Item>
                <Form.Item label="Bar Chart">
                    <Switch
                        className="jira-dashboard-select"
                        onChange={handleChartSelection}
                    >
                        Bar Chart
                    </Switch>
                </Form.Item>
            </Form>
            {chartData.length > 0 ? (
                <Chart
                    className="charts"
                    chartType={chartType}
                    data={chartData}
                    options={{
                        title: "Analytics",
                        ...columnChartConfigurationOptions,
                        chartArea: {
                            ...columnChartConfigurationOptions.chartArea,
                        },
                        hAxis: {
                            minValue: 0,
                        },
                        curveType: "function",
                    }}
                />
            ) : (
                <Skeleton
                    active
                    title={false}
                    paragraph={{ rows: 20, width: 40 }}
                />
            )}
        </Card>
    );
}
export { HotelAnalytics };
